import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AppShell } from "../../components/Sidebar";
import { api } from "../../lib/api";
import { ArrowLeft, CheckCircle2, Loader2, Eye, EyeOff, Check } from "lucide-react";
import { toast } from "sonner";

export default function ChangePassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // form | updating | success
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");

  const checks = [
    { ok: next.length >= 8, label: "Use at least 8 characters" },
    { ok: /[A-Z]/.test(next) && /[a-z]/.test(next), label: "Include uppercase and lowercase letters" },
    { ok: /\d/.test(next), label: "Include numbers" },
    { ok: /[^A-Za-z0-9]/.test(next), label: "Include symbols" },
    { ok: next.length > 0 && !/password|123|qwerty/i.test(next), label: "Avoid common words or personal information" },
  ];
  const allValid = checks.every((c) => c.ok);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allValid) return setError("Please meet all password recommendations.");
    if (next !== confirm) return setError("New passwords don't match.");
    setStep("updating");
    try {
      await api.post("/account/change-password", { current_password: current, new_password: next });
      setStep("success");
    } catch (err) {
      toast.error("Failed: current password may be incorrect");
      setStep("form");
      setError(err?.response?.data?.detail || "Could not update password.");
    }
  };

  return (
    <AppShell role="teacher">
      <Link to="/teacher/settings" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft size={14} /> Back to Settings
      </Link>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900">Change Password</h1>
      <p className="mt-1 text-slate-500">Pick a strong password you'll remember.</p>

      <div className="mt-6 grid max-w-3xl gap-6 lg:grid-cols-[1fr_300px]">
        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
          {step === "form" && (
            <form onSubmit={submit} className="space-y-4">
              <Field label="Current Password">
                <PwInput data-testid="current-password" value={current} onChange={setCurrent} show={show} />
              </Field>
              <Field label="New Password">
                <PwInput data-testid="new-password" value={next} onChange={setNext} show={show} />
              </Field>
              <Field label="Confirm New Password">
                <PwInput data-testid="confirm-password" value={confirm} onChange={setConfirm} show={show} />
              </Field>

              <label className="flex items-center gap-2 text-sm text-slate-500">
                <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
                Show passwords
              </label>

              {error && <p className="text-sm text-rose-600">{error}</p>}
              <button data-testid="submit-change-password" className="w-full rounded-xl bg-indigo-600 py-3 font-semibold text-white shadow hover:bg-indigo-700">Update Password</button>
            </form>
          )}

          {step === "updating" && (
            <div className="flex flex-col items-center py-12">
              <Loader2 className="animate-spin text-indigo-600" size={42} />
              <p className="mt-4 text-sm text-slate-600">Updating password…</p>
            </div>
          )}

          {step === "success" && (
            <div className="flex flex-col items-center py-8">
              <div className="rounded-full bg-emerald-100 p-4 text-emerald-600"><CheckCircle2 size={42} /></div>
              <h2 className="mt-4 text-xl font-bold text-emerald-700">Password Changed Successfully!</h2>
              <p className="mt-1 text-sm text-slate-500">Use your new password the next time you log in.</p>
              <button onClick={() => navigate("/teacher/settings")} data-testid="back-to-settings-pw" className="mt-5 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700">Back to Settings</button>
            </div>
          )}
        </div>

        <aside className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card">
          <h3 className="text-sm font-bold text-slate-900">Safe password recommendations</h3>
          <ul className="mt-3 space-y-2.5">
            {checks.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className={`mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full ${c.ok ? 'bg-emerald-500 text-white' : 'border border-slate-200 bg-white text-slate-400'}`}>
                  {c.ok && <Check size={12} />}
                </span>
                <span className={c.ok ? "text-slate-700" : "text-slate-500"}>{c.label}</span>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </AppShell>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>{children}</label>
);

const PwInput = ({ value, onChange, show, ...rest }) => (
  <div className="relative">
    <input
      type={show ? "text" : "password"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 pr-11 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
      {...rest}
    />
  </div>
);
